import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserCircle, Phone, Landmark, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

// Bu sayfa kimlik dogrulamasi GEREKTIRMEZ — personel linki token ile cozulur.
// `lib/api.js`'teki JWT'li istemci KASITLI olarak kullanilmiyor (bkz.
// PublicDailyReportForm.js'teki ayni not): o ortak istemciye ileride "yetki
// gerekli" mantigi eklenirse bu sayfa sessizce kirilmasin.
const API_BASE = window.location.origin + '/api/erp/hr';

// Ekran etiketleri ve alan turleri. Sunucu hangi alanlarin doldurulabilecegini
// soyler (beyaz liste), burasi yalnizca onlari NASIL gosterecegimizi tanimlar.
const ETIKET = {
  first_name: 'Ad', last_name: 'Soyad', national_id: 'Kimlik No',
  birth_date: 'Doğum Tarihi', gender: 'Cinsiyet',
  phone: 'Telefon', email: 'E-posta', address: 'Adres',
  emergency_contact_name: 'Acil Durumda Aranacak Kişi',
  emergency_contact_phone: 'Acil Durum Telefonu',
  bank_name: 'Banka', bank_account_number: 'IBAN / Hesap No',
  insurance_number: 'Sosyal Sigorta No',
};
const IPUCU = {
  national_id: 'Kimlik kartınızdaki numara',
  phone: 'Size ulaşabileceğimiz numara',
  bank_account_number: 'Maaşınızın yatacağı hesap',
  emergency_contact_phone: 'Yakınınızın numarası',
};
const TIP = { birth_date: 'date', email: 'email', phone: 'tel',
              emergency_contact_phone: 'tel' };
const COK_SATIR = ['address'];

const GRUPLAR = [
  { anahtar: 'kisisel', baslik: 'Kişisel Bilgiler', Ikon: UserCircle },
  { anahtar: 'iletisim', baslik: 'İletişim Bilgileri', Ikon: Phone },
  { anahtar: 'finansal', baslik: 'Banka Bilgileri', Ikon: Landmark },
];

export default function PublicEmployeeForm() {
  const { token } = useParams();
  const [yukleniyor, setYukleniyor] = useState(true);
  const [gecersiz, setGecersiz] = useState(null);
  const [sirket, setSirket] = useState('');
  const [alanlar, setAlanlar] = useState({});
  const [zorunlu, setZorunlu] = useState([]);
  const [deger, setDeger] = useState({});
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [gonderildi, setGonderildi] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/personel-formu/${token}`);
        if (!res.ok) {
          const g = await res.json().catch(() => ({}));
          setGecersiz(g.detail || 'Bu bağlantı geçerli değil.');
          setYukleniyor(false);
          return;
        }
        const d = await res.json();
        setSirket(d.sirket_adi || '');
        setAlanlar(d.alanlar || {});
        setZorunlu(d.zorunlu || []);
        setDeger(d.mevcut || {});
      } catch {
        setGecersiz('Bağlantı kurulamadı. İnternetinizi kontrol edip tekrar deneyin.');
      }
      setYukleniyor(false);
    })();
  }, [token]);

  const yaz = (alan, v) => setDeger(p => ({ ...p, [alan]: v }));

  const gonder = async () => {
    const eksik = zorunlu.filter(a => !(deger[a] || '').trim());
    if (eksik.length) {
      toast.error('Şu alanlar zorunlu: ' + eksik.map(a => ETIKET[a] || a).join(', '));
      document.querySelector(`[data-alan="${eksik[0]}"]`)?.focus();
      return;
    }
    // Gonderdikten sonra link kapaniyor — geri donusu olmayan islem, onay alalim.
    if (!window.confirm(
      'Bilgileriniz kaydedilecek ve bu bağlantı kapanacak.\n\n' +
      'Gönderdikten sonra düzeltme gerekirse yetkiliden yeni bağlantı istemeniz gerekir.\n\n' +
      'Göndermek istiyor musunuz?')) return;

    setGonderiliyor(true);
    try {
      const res = await fetch(`${API_BASE}/personel-formu/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deger),
      });
      if (!res.ok) {
        const g = await res.json().catch(() => ({}));
        toast.error(g.detail || 'Gönderilemedi, lütfen tekrar deneyin.');
        setGonderiliyor(false);
        return;
      }
      setGonderildi(true);
    } catch {
      toast.error('Gönderilemedi. İnternetinizi kontrol edip tekrar deneyin.');
    }
    setGonderiliyor(false);
  };

  if (yukleniyor) {
    return (
      <div className="min-h-screen bg-[#f5f1e8] flex items-center justify-center p-6">
        <p className="text-[#6b5f4f]">Form yükleniyor…</p>
      </div>
    );
  }

  if (gecersiz) {
    return (
      <div className="min-h-screen bg-[#f5f1e8] flex items-center justify-center p-6">
        <div className="bg-white border border-[#d0c4b0] rounded-lg p-6 max-w-sm w-full text-center">
          <AlertTriangle size={34} className="text-[#7a3535] mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-[#1a1916] mb-2">Bağlantı açılamadı</h1>
          <p className="text-sm text-[#6b5f4f]">{gecersiz}</p>
        </div>
      </div>
    );
  }

  if (gonderildi) {
    return (
      <div className="min-h-screen bg-[#f5f1e8] flex items-center justify-center p-6">
        <div className="bg-white border border-[#d0c4b0] rounded-lg p-6 max-w-sm w-full text-center">
          <CheckCircle size={40} className="text-[#3d6e4e] mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-[#1a1916] mb-2">Teşekkürler</h1>
          <p className="text-sm text-[#6b5f4f]">
            Bilgileriniz kaydedildi. Bu bağlantı artık kapandı; düzeltme gerekirse
            yetkiliden yeni bağlantı isteyebilirsiniz.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f1e8] pb-28">
      <div className="max-w-lg mx-auto px-4">
        <header className="pt-8 pb-5">
          <p className="text-xs uppercase tracking-wider text-[#8a7e70] mb-1">{sirket}</p>
          <h1 className="text-2xl font-semibold text-[#1a1916] leading-tight">
            Personel Bilgi Formu
          </h1>
          <p className="text-sm text-[#6b5f4f] mt-2">
            Bilgilerinizi doldurup <strong>Gönder</strong>e basın. Form yalnızca bir kez
            gönderilebilir.
          </p>
        </header>

        {GRUPLAR.map(({ anahtar, baslik, Ikon }) => {
          const grupAlanlari = alanlar[anahtar] || [];
          if (!grupAlanlari.length) return null;
          return (
            <section key={anahtar} className="bg-white border border-[#d0c4b0] rounded-lg mb-4">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-[#e8dfd0]">
                <Ikon size={16} className="text-[#4a5a3a]" />
                <h2 className="text-sm font-semibold text-[#1a1916]">{baslik}</h2>
              </div>
              <div className="p-4 flex flex-col gap-4">
                {grupAlanlari.map(alan => (
                  <div key={alan}>
                    <Label htmlFor={alan} className="text-xs text-[#6b5f4f]">
                      {ETIKET[alan] || alan}
                      {zorunlu.includes(alan) && <span className="text-[#7a3535] ml-1">*</span>}
                    </Label>
                    {COK_SATIR.includes(alan) ? (
                      <textarea id={alan} data-alan={alan} rows={3}
                        value={deger[alan] || ''}
                        onChange={e => yaz(alan, e.target.value)}
                        /* 16px taban: mobil tarayicilar daha kucuk yazi tipinde
                           alana odaklanildiginda sayfayi otomatik yakinlastiriyor */
                        className="mt-1 w-full text-base rounded-md border border-[#d0c4b0] bg-white px-3 py-2 text-[#1a1916]"
                      />
                    ) : alan === 'gender' ? (
                      <select id={alan} data-alan={alan}
                        value={deger[alan] || ''}
                        onChange={e => yaz(alan, e.target.value)}
                        className="mt-1 w-full h-11 text-base rounded-md border border-[#d0c4b0] bg-white px-3 text-[#1a1916]">
                        <option value="">Seçiniz</option>
                        <option value="kadin">Kadın</option>
                        <option value="erkek">Erkek</option>
                        <option value="belirtmek_istemiyorum">Belirtmek istemiyorum</option>
                      </select>
                    ) : (
                      <Input id={alan} data-alan={alan}
                        type={TIP[alan] || 'text'}
                        inputMode={TIP[alan] === 'tel' ? 'tel' : undefined}
                        value={deger[alan] || ''}
                        onChange={e => yaz(alan, e.target.value)}
                        className="mt-1 h-11 text-base border-[#d0c4b0]"
                      />
                    )}
                    {IPUCU[alan] && (
                      <p className="text-[11px] text-[#8a7e70] mt-1">{IPUCU[alan]}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })}

        <p className="text-[11px] text-[#8a7e70] px-1 mb-2">
          Bu form yalnızca kişisel, iletişim ve banka bilgilerinizi içerir.
          Maaş ve pozisyon bilgileri burada yer almaz.
        </p>
      </div>

      {/* Gonder dugmesi ekranin altina sabit: uzun formda telefonda asagi
          kaydirmadan ulasilabilsin. */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#d0c4b0] p-3">
        <div className="max-w-lg mx-auto">
          <Button onClick={gonder} disabled={gonderiliyor}
            className="w-full h-12 text-base bg-[#4a5a3a] hover:bg-[#3d4a30] text-white"
            data-testid="personel-form-gonder">
            {gonderiliyor ? 'Gönderiliyor…' : 'Gönder'}
          </Button>
        </div>
      </div>
    </div>
  );
}
